export async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
    }
    return response.json();
}

/**
 * Extracts a user-friendly error message from an API error response.
 * Handles validation errors from FluentValidation (400 responses) and other error formats.
 * @param error The error object caught from an API call
 * @param fallbackMessage The default message to show if no specific error can be extracted
 * @param fieldOrder Optional array of field names to sort errors by (matches validator rule order)
 * @returns A string containing the error message(s), formatted as bullet list if multiple
 */
export function extractApiErrorMessage(
    error: any,
    fallbackMessage: string = 'An unexpected error occurred',
    fieldOrder?: string[]
): string {
    const data = error?.response?.data;

    if (!data) {
        return error?.message || fallbackMessage;
    }

    // Handle FluentValidation errors format: { errors: { field: ["message1", "message2"] } }
    if (data.errors && typeof data.errors === 'object') {
        const errorMessages: string[] = [];

        // Sort fields by the predefined order if provided, unknown fields go at the end
        let fields = Object.keys(data.errors);
        if (fieldOrder) {
            fields = fields.sort((a, b) => {
                const indexA = fieldOrder.indexOf(a);
                const indexB = fieldOrder.indexOf(b);
                const orderA = indexA === -1 ? fieldOrder.length : indexA;
                const orderB = indexB === -1 ? fieldOrder.length : indexB;
                return orderA - orderB;
            });
        }

        for (const field of fields) {
            const fieldErrors = data.errors[field];
            if (Array.isArray(fieldErrors)) {
                errorMessages.push(...fieldErrors);
            } else if (typeof fieldErrors === 'string') {
                errorMessages.push(fieldErrors);
            }
        }

        if (errorMessages.length > 0) {
            // Format as bullet list if multiple errors, otherwise return single message
            if (errorMessages.length === 1) {
                return errorMessages[0];
            }
            return errorMessages.map(msg => `• ${msg}`).join('\n');
        }
    }

    // Handle simple message format: { message: "Error message" }
    if (data.message) {
        return data.message;
    }

    // Handle title (ASP.NET format)
    if (data.title) {
        return data.title;
    }

    // Handle ASP.NET Identity errors format: [{code, description}] or plain string array ["error1", "error2"]
    if (Array.isArray(data)) {
        // Check for object format with description property
        const descriptions = data
            .filter(item => item.description)
            .map(item => item.description);
        if (descriptions.length > 0) {
            return descriptions.length === 1
                ? descriptions[0]
                : descriptions.map(msg => `• ${msg}`).join('\n');
        }

        // Handle plain string array format
        const stringErrors = data.filter(item => typeof item === 'string');
        if (stringErrors.length > 0) {
            return stringErrors.length === 1
                ? stringErrors[0]
                : stringErrors.map(msg => `• ${msg}`).join('\n');
        }
    }

    // Handle string response
    if (typeof data === 'string') {
        return data;
    }

    // Handle Error objects with message
    if (error?.message) {
        return error.message;
    }

    return fallbackMessage;
} 